// Aba "Atividades" — visão Calendário (grid 7×N mensal).
// Wave 2 (Antd) — 2026-06-01. Grid mantida custom (calendário não tem
// equivalente leve em Antd que cubra esse layout específico).

import { useEffect, useState } from 'react';
import { Button, Typography, Flex, Space, Card } from 'antd';
import { LeftOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';
import { hojeIso } from '../../utils/dataFmt';
import type { Atividade } from './types';

export function CalendarioAtividadesOp({ atividades, onEditar }: {
  atividades: Atividade[];
  onEditar: (a: Atividade) => void;
}) {
  const [mes, setMes] = useState<Date>(() => {
    const raw = sessionStorage.getItem('op_calendario_mes');
    if (raw) {
      const d = new Date(raw);
      if (Number.isFinite(d.getTime())) return d;
    }
    const h = new Date(); h.setDate(1); return h;
  });
  useEffect(() => { sessionStorage.setItem('op_calendario_mes', mes.toISOString()); }, [mes]);

  const porDia: Record<string, Atividade[]> = {};
  for (const a of atividades) {
    const src = a.data_termino_previsto || a.data_inicio_prevista;
    if (!src) continue;
    const dia = String(src).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) continue;
    (porDia[dia] = porDia[dia] || []).push(a);
  }
  const semPrazo = atividades.filter(a => !a.data_termino_previsto && !a.data_inicio_prevista);

  const ano = mes.getFullYear();
  const mesNum = mes.getMonth();
  const primeiroDia = new Date(ano, mesNum, 1);
  const ultimoDia = new Date(ano, mesNum + 1, 0);
  const inicio = new Date(primeiroDia);
  inicio.setDate(inicio.getDate() - inicio.getDay());
  const dias: Date[] = [];
  for (const d = new Date(inicio); d <= ultimoDia || d.getDay() !== 0; d.setDate(d.getDate() + 1)) {
    dias.push(new Date(d));
    if (dias.length > 42) break;
  }
  const hojeStr = hojeIso();
  const nomeMes = primeiroDia.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const cor: Record<string, { bg: string; fg: string }> = {
    pendente:     { bg: '#F0F1F5', fg: '#444' },
    em_andamento: { bg: '#E7ECFD', fg: '#0930D0' },
    concluida:    { bg: '#E8F5EE', fg: '#0a7f4a' },
    bloqueada:    { bg: '#FDECEC', fg: '#B91626' },
    cancelada:    { bg: '#F2F2F4', fg: '#888' },
  };

  return (
    <div>
      <Flex align="center" gap={8} style={{ marginBottom: 10 }}>
        <Button icon={<LeftOutlined />} size="small"
          onClick={() => setMes(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))} />
        <Typography.Text strong style={{ fontSize: 13, color: '#020D7F',
          textTransform: 'capitalize', minWidth: 160, textAlign: 'center' }}>
          {nomeMes}
        </Typography.Text>
        <Button icon={<RightOutlined />} size="small"
          onClick={() => setMes(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))} />
        <Button size="small" onClick={() => { const h = new Date(); h.setDate(1); setMes(h); }}>
          Hoje
        </Button>
        <div style={{ flex: 1 }} />
        <Typography.Text type="secondary" style={{ fontSize: 10 }}>
          {atividades.length} atividade(s) · {semPrazo.length} sem prazo
        </Typography.Text>
      </Flex>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1,
        background: '#e0e0e0', borderRadius: 6, overflow: 'hidden' }}>
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
          <div key={d} style={{
            background: '#020D7F', color: '#fff', padding: '6px 8px',
            fontSize: 10, fontWeight: 700, textAlign: 'center', letterSpacing: 0.5,
          }}>{d}</div>
        ))}
        {dias.map((d, i) => {
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const noMes = d.getMonth() === mesNum;
          const ehHoje = iso === hojeStr;
          const ativsDia = porDia[iso] || [];
          return (
            <div key={i} style={{
              background: ehHoje ? '#FFF8E8' : noMes ? '#fff' : '#fafafa',
              minHeight: 96, padding: 4,
              opacity: noMes ? 1 : 0.55,
              border: ehHoje ? '1px solid #B07500' : 'none',
            }}>
              <div style={{
                fontSize: 11, fontWeight: ehHoje ? 700 : 500,
                color: ehHoje ? '#B07500' : '#444', marginBottom: 3,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>{d.getDate()}</span>
                {ativsDia.length > 3 && (
                  <Typography.Text strong style={{ fontSize: 9, color: '#0930D0' }}>
                    +{ativsDia.length - 3}
                  </Typography.Text>
                )}
              </div>
              {ativsDia.slice(0, 3).map(a => {
                const c = cor[a.status] || cor.pendente;
                return (
                  <div key={a.id} onClick={() => onEditar(a)}
                    title={`${a.titulo} — ${a.status}`}
                    style={{
                      padding: '2px 5px', marginBottom: 2, fontSize: 10,
                      background: c.bg, color: c.fg,
                      borderRadius: 3, cursor: 'pointer',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      fontWeight: 500,
                    }}>{a.titulo}</div>
                );
              })}
            </div>
          );
        })}
      </div>

      {semPrazo.length > 0 && (
        <Card size="small" style={{ marginTop: 12, background: '#FFF8E8', borderColor: '#B07500', borderStyle: 'dashed' }}
          styles={{ body: { padding: 10 } }}>
          <Typography.Text strong style={{ fontSize: 11, color: '#B07500', display: 'block', marginBottom: 6 }}>
            <WarningOutlined /> {semPrazo.length} atividade(s) sem prazo
          </Typography.Text>
          <Space size={4} wrap>
            {semPrazo.map(a => {
              const c = cor[a.status] || cor.pendente;
              return (
                <span key={a.id} onClick={() => onEditar(a)}
                  style={{
                    padding: '3px 8px', fontSize: 10, background: c.bg, color: c.fg,
                    borderRadius: 3, cursor: 'pointer', fontWeight: 500,
                  }}>{a.titulo}</span>
              );
            })}
          </Space>
        </Card>
      )}

      <Flex gap={12} wrap style={{ marginTop: 10 }}>
        {Object.entries(cor).map(([s, c]) => (
          <Space key={s} size={4}>
            <span style={{ width: 12, height: 12, background: c.bg,
              border: `1px solid ${c.fg}`, borderRadius: 2, display: 'inline-block' }} />
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>{s.replace('_', ' ')}</Typography.Text>
          </Space>
        ))}
      </Flex>
    </div>
  );
}
